import { z } from 'zod';
import { AttendeePreviewDto } from './attendee-preview';
import { ClubThinRefDto } from './club-ref';
import { EventCategoryDto } from './event-category';
import { EventHashtagsRequiredSchema } from './event-hashtags';
import { PartnerProgramDto, PartnerProgramInput } from './partner-program';
import { PlaceThinRefDto } from './place';
import { PaymentDetailsText } from './ticket-payment';

// Telegram username normalizer: strips leading '@', trims, lowercases.
const normalizeUsername = (v: unknown) => {
  if (typeof v !== 'string') return v;
  const trimmed = v.trim();
  const noAt = trimmed.startsWith('@') ? trimmed.slice(1) : trimmed;
  return noAt.toLowerCase();
};

const usernameString = z
  .string()
  .min(5)
  .max(32)
  .regex(/^[a-z0-9_]+$/, 'invalid telegram username');

/** Telegram-ник: без `@`, в нижнем регистре, 5–32 символа `[a-z0-9_]`. */
export const TelegramUsernameInput = z.preprocess(normalizeUsername, usernameString);

const organizerUsername = z.preprocess(normalizeUsername, usernameString).optional();

// Manage flow allows clearing the organizer by sending null.
const organizerUsernameNullable = z.preprocess(normalizeUsername, usernameString.nullable()).optional();

// `== null` покрывает и undefined (поле не пришло), и null (стирание окончания
// в manage-флоу) — пара обязана быть однородной в любом из этих смыслов.
const endsTogether = (v: {
  endsAtLocalDate?: string | null | undefined;
  endsAtLocalTime?: string | null | undefined;
}) => (v.endsAtLocalDate == null) === (v.endsAtLocalTime == null);

const endsTogetherMessage = 'endsAtLocal{Date,Time} must be provided together';

/** Максимум категорий у события (create / manage / admin). */
export const EVENT_CATEGORY_MAX_COUNT = 2;

// Internal base shape — Zod 4 forbids .omit()/.partial() on refined schemas,
// so we keep the unrefined ZodObject around to derive EventUpdateInput from it.
const eventCreateBase = z.object({
  cityId: z.uuid(),
  title: z.string().min(1).max(200).trim(),
  description: z.string().max(5000).optional(),
  venueText: z.string().min(1).max(300).trim(),

  startsAtLocalDate: z.iso.date(),
  startsAtLocalTime: z
    .string()
    .regex(/^\d{2}:\d{2}$/, 'HH:MM')
    .nullable()
    .optional(),
  endsAtLocalDate: z.iso.date().optional(),
  endsAtLocalTime: z
    .string()
    .regex(/^\d{2}:\d{2}$/, 'HH:MM')
    .optional(),

  price: z.number().int().nonnegative().nullable().optional(),
  maxAttendees: z.number().int().positive().nullable().optional(),

  organizerUsername,
  coverImageUrl: z.url().max(2000).optional(),
  videoUrl: z.url().max(2000).optional(),
  placeId: z.uuid().nullable().optional(),

  categoryIds: z.array(z.uuid()).min(1).max(EVENT_CATEGORY_MAX_COUNT),

  bookingExtraInfo: z.string().max(5000).optional(),
  reminderText: z.string().max(2000).optional(),

  publish: z.boolean().default(false),
});

export const EventCreateInput = eventCreateBase.refine(endsTogether, {
  message: endsTogetherMessage,
});
export type EventCreateInput = z.infer<typeof EventCreateInput>;

export const EventUpdateInput = eventCreateBase
  .omit({ publish: true })
  .partial()
  .extend({
    isVerified: z.boolean().optional(),
    isRecommended: z.boolean().optional(),
  })
  .refine(endsTogether, { message: endsTogetherMessage });
export type EventUpdateInput = z.infer<typeof EventUpdateInput>;

const gmapsUrl = z.string().min(1).max(2000);

export const EventManageLocationInput = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('place'), placeId: z.uuid() }),
  // venueText — опционален: клиент уже знает человекочитаемый текст места (имя
  // найденного заведения либо дефолтная подпись «Место на карте» для пина без
  // привязки/тапа по карте) — бэку не из чего его синтезировать, если Google
  // не резолвит ссылку до настоящего Place (голые координаты без названия).
  z.object({
    mode: z.literal('gmaps'),
    url: gmapsUrl,
    venueText: z.string().trim().min(1).max(200).optional(),
  }),
  z.object({ mode: z.literal('custom'), venueText: z.string().trim().min(1).max(200) }),
]);

const manageStartsAtTogether = (v: {
  startsAtLocalDate?: string | undefined;
  startsAtLocalTime?: string | undefined;
}) => (v.startsAtLocalDate == null) === (v.startsAtLocalTime == null);

const manageStartsAtTogetherMessage = 'startsAtLocalDate and startsAtLocalTime must be provided together';

export const EventManageUpdateInput = z
  .object({
    title: z.string().min(1).max(200).trim().optional(),
    description: z.string().max(5000).nullable().optional(),
    location: EventManageLocationInput.optional(),
    startsAtLocalDate: z.iso.date().optional(),
    startsAtLocalTime: z
      .string()
      .regex(/^\d{2}:\d{2}$/, 'HH:MM')
      .optional(),
    // Окончание правится парой; null-пара стирает endsAt.
    endsAtLocalDate: z.iso.date().nullable().optional(),
    endsAtLocalTime: z
      .string()
      .regex(/^\d{2}:\d{2}$/, 'HH:MM')
      .nullable()
      .optional(),
    price: z.number().int().nonnegative().nullable().optional(),
    maxAttendees: z.number().int().positive().nullable().optional(),
    isDonation: z.boolean().optional(),
    /** Реквизиты для оплаты переводом; null — выключить продажу. */
    paymentDetails: PaymentDetailsText.nullable().optional(),
    /** null — выключить программу; не задано — не трогать. */
    partnerProgram: PartnerProgramInput.nullable().optional(),
    organizerUsername: organizerUsernameNullable,
    coverImageUrl: z.url().max(2000).nullable().optional(),
    videoUrl: z.url().max(2000).nullable().optional(),
    categoryIds: z.array(z.uuid()).min(1).max(EVENT_CATEGORY_MAX_COUNT).optional(),
    bookingExtraInfo: z.string().max(5000).nullable().optional(),
    reminderText: z.string().max(2000).nullable().optional(),
    hashtags: EventHashtagsRequiredSchema.optional(),
  })
  .refine(manageStartsAtTogether, { message: manageStartsAtTogetherMessage })
  .refine(endsTogether, { message: endsTogetherMessage });
export type EventManageUpdateInput = z.infer<typeof EventManageUpdateInput>;

// Создание события обычным юзером (mini-app).
// Город берётся из профиля юзера (не в payload). По умолчанию видимость PUBLIC;
// владелец клуба может опубликовать событие от имени клуба — см. visibility ниже.
// Место — тот же discriminated union, что в manage-флоу (place | gmaps).
export const CreateUserEventInput = z
  .object({
    title: z.string().min(1).max(200).trim(),
    description: z.string().max(5000).optional(),
    startsAtLocalDate: z.iso.date(),
    startsAtLocalTime: z
      .string()
      .regex(/^\d{2}:\d{2}$/, 'HH:MM')
      .nullable()
      .optional(),
    endsAtLocalDate: z.iso.date().optional(),
    endsAtLocalTime: z
      .string()
      .regex(/^\d{2}:\d{2}$/, 'HH:MM')
      .optional(),
    location: EventManageLocationInput,
    categoryIds: z.array(z.uuid()).min(1).max(EVENT_CATEGORY_MAX_COUNT),
    price: z.number().int().nonnegative().nullable().optional(),
    isDonation: z.boolean().optional(),
    /** Реквизиты для оплаты переводом; не задано/null — без продажи. */
    paymentDetails: PaymentDetailsText.nullable().optional(),
    partnerProgram: PartnerProgramInput.optional(),
    maxAttendees: z.number().int().positive().nullable().optional(),
    bookingExtraInfo: z.string().max(5000).optional(),
    reminderText: z.string().max(2000).optional(),
    hashtags: EventHashtagsRequiredSchema,
    copyCoverFromEventId: z.uuid().optional(),
    /** Клуб-владелец события; обязателен, если visibility='CLUB' (проверяет сервис). */
    clubId: z.uuid().optional(),
    // CONNECTIONS и PRIVATE зарезервированы под networking-встречи и здесь
    // недоступны — обычный юзер создаёт только PUBLIC- или CLUB-событие,
    // расширять этот enum до networking-значений не нужно.
    visibility: z.enum(['PUBLIC', 'CLUB']).optional(),
  })
  .refine(endsTogether, { message: endsTogetherMessage });
export type CreateUserEventInput = z.infer<typeof CreateUserEventInput>;

export const EventListQuery = z.object({
  cityId: z.uuid().optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  categoryId: z.uuid().optional(),
  // Матчит title ИЛИ description — так для публичного списка (EventsService.listPublic);
  // админский список (listAdmin) ищет только по title, это осознанное расхождение.
  search: z.string().max(200).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type EventListQuery = z.infer<typeof EventListQuery>;

export const EventStatus = z.enum(['DRAFT', 'PENDING_MODERATION', 'PUBLISHED', 'ARCHIVED']);
export type EventStatus = z.infer<typeof EventStatus>;

export const AttendanceStatus = z.enum(['CONFIRMED', 'WAITLISTED']);
export type AttendanceStatus = z.infer<typeof AttendanceStatus>;

export const MyAttendanceStatus = z.enum([
  'NONE',
  'CONFIRMED',
  'WAITLISTED',
  'PENDING',
  'INVITED',
  'DECLINED',
]);
export type MyAttendanceStatus = z.infer<typeof MyAttendanceStatus>;

export const AttendanceMutationResult = z.object({
  attendanceCount: z.number().int(),
  status: MyAttendanceStatus,
});
export type AttendanceMutationResult = z.infer<typeof AttendanceMutationResult>;

export const EventAttendeePreviewDto = AttendeePreviewDto;
export type EventAttendeePreviewDto = AttendeePreviewDto;

export const EventDto = z.object({
  id: z.uuid(),
  cityId: z.uuid(),
  title: z.string(),
  description: z.string().nullable(),
  hashtags: z.array(z.string()).max(3).default([]),
  venueText: z.string().nullable(),
  externalUrl: z.string().nullable().optional(),
  startsAt: z.iso.datetime(),
  hasStartTime: z.boolean(),
  endsAt: z.iso.datetime().nullable(),
  price: z.number().int().nullable(),
  isDonation: z.boolean(),
  /** Продажа билетов: P2P — по реквизитам с чеком (isP2pSaleOn). */
  ticketPayment: z.enum(['NONE', 'P2P']),
  /** Реквизиты — только организатору события (и в админке); остальным null. */
  paymentDetails: z.string().nullable(),
  /** Партнёрская программа; null — выключена. */
  partnerProgram: PartnerProgramDto.nullable(),
  bookingExtraInfo: z.string().nullable(),
  reminderText: z.string().nullable(),
  currency: z.string().length(3),
  timeZone: z.string(),
  organizerUsername: z.string().nullable(),
  organizerUserId: z.uuid().nullable(),
  coverImageUrl: z.string().nullable(),
  galleryImageUrls: z.array(z.string()).default([]),
  isVerified: z.boolean().default(false),
  isRecommended: z.boolean().default(false),
  videoUrl: z.string().nullable(),
  place: PlaceThinRefDto.nullable(),
  /**
   * Клуб-владелец события («метка клуба» в ленте и на карточке); null — обычное
   * городское событие. Стоит и на PUBLIC-событии клуба (воронка в членство), и
   * на `visibility='CLUB'` — но последнее не-член и так не получает ни по одному
   * из пяти путей чтения, так что поле ничего дополнительно не открывает.
   */
  club: ClubThinRefDto.nullable(),
  status: EventStatus,
  publishedAt: z.iso.datetime().nullable(),
  archivedAt: z.iso.datetime().nullable(),
  attendanceCount: z.number().int(),
  isAttendingMe: z.boolean(),
  maxAttendees: z.number().int().nullable(),
  isFull: z.boolean(),
  myAttendanceStatus: MyAttendanceStatus,
  // ТЗ §1.2: подписан ли текущий юзер на организатора этого ивента («Пойду в следующий раз»).
  myFollowsOrganizer: z.boolean(),
  // likeCount = реальные лайки + seed-«накрутка» 3..7; isLikedByMe — реальный лайк текущего юзера.
  likeCount: z.number().int(),
  isLikedByMe: z.boolean(),
  categories: z.array(EventCategoryDto),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  visibility: z.enum(['PUBLIC', 'CONNECTIONS', 'PRIVATE', 'CLUB']),
  kind: z.enum(['EVENT', 'MEETING']),
  // true, если юзер открыл PRIVATE-мероприятие по ?via=<shortCode> и ещё не
  // attended — UI показывает кнопку «Принять приглашение» вместо «Я пойду».
  requiresShareAccept: z.boolean(),
  /** До 10 подтверждённых участников для превью «Идут» на карточке. */
  attendeesPreview: z.array(EventAttendeePreviewDto).max(10).default([]),
});
export type EventDto = z.infer<typeof EventDto>;

export const EventListItemDto = EventDto;
export type EventListItemDto = z.infer<typeof EventListItemDto>;

export const EventListResponse = z.object({
  items: z.array(EventListItemDto),
  nextCursor: z.string().nullable(),
});
export type EventListResponse = z.infer<typeof EventListResponse>;

export const MyEventsQuery = z.object({
  scope: z.enum(['upcoming', 'past']),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type MyEventsQuery = z.infer<typeof MyEventsQuery>;

export const AdminEventListQuery = EventListQuery.extend({
  status: EventStatus.optional(),
});
export type AdminEventListQuery = z.infer<typeof AdminEventListQuery>;

/**
 * Ссылка на исходное Telegram-сообщение, из которого был создан Event через
 * channel-import / import-queue. Public DTO её не несёт — поле живёт только
 * на админ-эндпоинтах, чтобы не светить источники мини-апа пользователям.
 */
export const SourceMessageRef = z.object({
  id: z.uuid(),
  postUrl: z.string(),
  telegramMessageId: z.number().int(),
  postedAt: z.iso.datetime(),
  channelId: z.uuid(),
  channelSlug: z.string(),
  channelTitle: z.string(),
});
export type SourceMessageRef = z.infer<typeof SourceMessageRef>;

export const AdminEventManagerDto = z.object({
  id: z.uuid(),
  username: z.string().nullable(),
  firstName: z.string().nullable(),
});
export type AdminEventManagerDto = z.infer<typeof AdminEventManagerDto>;

export const AdminEventDto = EventDto.extend({
  sourceMessage: SourceMessageRef.nullable(),
  rejectionReason: z.string().nullable(),
  /** Событие уже публиковалось: `PENDING_MODERATION` у такого — повторная проверка правки. */
  previouslyPublished: z.boolean(),
  /** Закреплённый менеджер (CITY_MODERATOR города, #166); актуальность роли не гарантируется. */
  manager: AdminEventManagerDto.nullable(),
});
export type AdminEventDto = z.infer<typeof AdminEventDto>;

/** Назначение менеджера события (#166). null — снять. */
export const AdminEventManagerBody = z.object({ managerUserId: z.uuid().nullable() });
export type AdminEventManagerBody = z.infer<typeof AdminEventManagerBody>;

/**
 * Элемент списка событий в админке (`GET admin/events`) — публичный
 * EventListItemDto плюс признак «уже публиковалось», чтобы модератор видел
 * бейдж повторной проверки прямо в списке, не открывая карточку. Публичным
 * клиентам (mini-app) это поле не отдаётся — см. EventListItemDto.
 */
export const AdminEventListItemDto = EventListItemDto.extend({
  /** Событие уже публиковалось: `PENDING_MODERATION` у такого — повторная проверка правки. */
  previouslyPublished: z.boolean(),
});
export type AdminEventListItemDto = z.infer<typeof AdminEventListItemDto>;

export const AdminEventListResponse = z.object({
  items: z.array(AdminEventListItemDto),
  nextCursor: z.string().nullable(),
});
export type AdminEventListResponse = z.infer<typeof AdminEventListResponse>;

export const RejectEventInput = z.object({
  reason: z.string().trim().min(1).max(500),
});
export type RejectEventInput = z.infer<typeof RejectEventInput>;

/** Статус ручного автопостинга события в городской чат (#161). */
export const AdminEventChatBroadcastStatusDto = z.object({
  chatTitle: z.string().nullable(),
  alreadySent: z.boolean(),
});
export type AdminEventChatBroadcastStatusDto = z.infer<typeof AdminEventChatBroadcastStatusDto>;

export const AdminEventChatBroadcastInput = z
  .object({
    forceResend: z.boolean().default(false),
  })
  .strict();
export type AdminEventChatBroadcastInput = z.infer<typeof AdminEventChatBroadcastInput>;

/** Лёгкий DTO для списка «на модерации / черновики» в «Моих событиях» (issue #85). */
export const MyModerationEventDto = z.object({
  id: z.uuid(),
  title: z.string(),
  status: EventStatus,
  rejectionReason: z.string().nullable(),
  startsAt: z.iso.datetime(),
  coverImageUrl: z.string().nullable(),
});
export type MyModerationEventDto = z.infer<typeof MyModerationEventDto>;

export const MyModerationEventsResponse = z.array(MyModerationEventDto);
export type MyModerationEventsResponse = z.infer<typeof MyModerationEventsResponse>;
